import { Injectable } from "@angular/core";

import { RemoteGatewayFactory } from "../../gateways/remote-gateway-factory";
import { ClusterSearchParameters } from "../../models/domains/pbm/clusters/cluster-search-parameters.model";
import { FiscalNumber } from "../../models/domains/pbm/fiscal-number";
import { AddIndustryProductDiscount as AddIndustryProductDiscount } from "../../models/domains/pbm/industry/add-industry-product-discount.model";
import { FileIndustryProductsDiscounts } from "../../models/domains/pbm/industry/file-industry-products-discounts.model";
import { ImportIndustryProductsDiscountsResult } from "../../models/domains/pbm/industry/import-industry-products-discounts-result.model";
import { IndustryDiscountPageableResult } from "../../models/domains/pbm/industry/industry-discount-pageable-result.model";
import { IndustryDiscountSearchParameters } from "../../models/domains/pbm/industry/industry-discount-search-parameters.model";
import { IndustryDiscount } from "../../models/domains/pbm/industry/industry-discount.model";
import { IndustryProductDiscounts } from "../../models/domains/pbm/industry/industry-product-discounts.model";
import { PbmBenefitBackendUrl } from "../../models/domains/urls/pbm-back-end.url";
import { ImportIndustryProductDiscountParser } from "../parsers/pbm/industry/import-industry-product-discount.parser";
import { IndustryDiscountParser } from "../parsers/pbm/industry/industry-discount.parser";
import { IndustryDiscountsPageableResultParser } from "../parsers/pbm/industry/industry-discounts-pageable-result.parser";
import { environment } from "src/environments/environment";
import { HttpHeaders } from "@angular/common/http";

@Injectable()
export class IndustryRepository {
  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private IndustryDiscountsPageableResultParser: IndustryDiscountsPageableResultParser,
    private importIndustryProductDiscountParser: ImportIndustryProductDiscountParser,
    private industryDiscountParser: IndustryDiscountParser,
  ) {}

  getDefaultHeaders() {
    return { "api-key": environment.pbmBenefitApiKey };
  }

  public async getIndustryDiscount(industryFiscalNumber: FiscalNumber): Promise<IndustryDiscount> {
    const headers = this.getDefaultHeaders();
    headers["Content-Type"] = "application/json";

    const body = JSON.stringify({
      IndustryFiscalNumber: industryFiscalNumber.getValue(),
    });

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new PbmBenefitBackendUrl("industry/discount"), body, headers);
      return await Promise.resolve(this.industryDiscountParser.parse(data));
    } catch (error) {
      return await Promise.reject(error.error.Data.ErrorMessage);
    }
  }

  public async findExistentIndustry(industryFiscalNumber: FiscalNumber): Promise<IndustryDiscount> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getUrlWithHeaders(
        new PbmBenefitBackendUrl("industry/discounts/find/" + industryFiscalNumber.getValue()).getUrl(),
        this.getDefaultHeaders(),
      );
      return await Promise.resolve(this.industryDiscountParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async getIndustryProductDiscounts(
    industryDiscountKey: string,
    industryDiscountSearchParameters: IndustryDiscountSearchParameters,
  ): Promise<IndustryDiscount> {
    const headers = this.getDefaultHeaders();
    headers["Content-Type"] = "application/json";

    const body = JSON.stringify(industryDiscountSearchParameters);

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new PbmBenefitBackendUrl("industry/discount/products/" + industryDiscountKey), body, headers);
      return await Promise.resolve(this.industryDiscountParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async getIndustryDiscountPageableResult(industryDiscountSearchParameters: IndustryDiscountSearchParameters): Promise<IndustryDiscountPageableResult> {
    const headers = this.getDefaultHeaders();
    headers["Content-Type"] = "application/json";

    const body = JSON.stringify(industryDiscountSearchParameters);

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new PbmBenefitBackendUrl("industry/discount/search"), body, headers);
      return await Promise.resolve(this.IndustryDiscountsPageableResultParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async updateIndustryDiscountsProduct(industryProductDiscounts: IndustryProductDiscounts): Promise<any> {
    const headers = this.getDefaultHeaders();
    headers["Content-Type"] = "application/json";

    const body = JSON.stringify(industryProductDiscounts);

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.putWithHeaders(new PbmBenefitBackendUrl("industry/discount/product"), body, headers);
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async addIndustryProductDiscount(addIndustryProductDiscount: AddIndustryProductDiscount): Promise<any> {
    const headers = this.getDefaultHeaders();
    headers["Content-Type"] = "application/json";

    const body = JSON.stringify({
      Ean: {
        Value: addIndustryProductDiscount.getEan(),
      },
      UserDiscount: addIndustryProductDiscount.getUserDiscount(),
      DirectDiscount: addIndustryProductDiscount.getDirectDiscount(),
      IndirectDiscount: addIndustryProductDiscount.getIndirectDiscount(),
    });

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(
        new PbmBenefitBackendUrl("industry/discount/product/" + addIndustryProductDiscount.getIndustryDiscountId()),
        body,
        headers,
      );
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error.error.Data.ErrorMessage);
    }
  }
  //aqui
  public async addIndustryProductDiscounts(fileIndustryProductsDiscounts: FileIndustryProductsDiscounts): Promise<ImportIndustryProductsDiscountsResult> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const header = new HttpHeaders(this.getDefaultHeaders());
      const data = await remoteGateway.postWithFileWithHeadersV2(
        new PbmBenefitBackendUrl("industry/discount/import/" + fileIndustryProductsDiscounts.getFiscalNumber().getValue()),
        fileIndustryProductsDiscounts.getFile(),
        header,
      );
      return await Promise.resolve(this.importIndustryProductDiscountParser.parse(data));
    } catch (error) {
      return await Promise.reject(this.importIndustryProductDiscountParser.parse(error));
    }
  }

  public async deleteProduct(industryProductDiscountKey: string): Promise<any> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.deleteWithHeaders(
        new PbmBenefitBackendUrl("industry/discount/product/" + industryProductDiscountKey),
        this.getDefaultHeaders(),
      );
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(this.importIndustryProductDiscountParser.parse(error));
    }
  }
}
